/*
One details window for a calendar page. Items carry data-pop; the caller builds the content.
Opens beside the item, easing in: on hover (desktop, a preview that keeps focus where it
is), on a tap or a click, and on Enter/Space — then focus moves into the window and Esc
brings it back to the item.
Closes on Esc, a click elsewhere, the pointer leaving, focus leaving, resize, or a scroll
that carries the item away (not the tail of a fling).
The window is a native popover (top layer) where the browser has one, a fixed block
otherwise; its position is measured only when it opens, never at page start. A bar fixed to
the bottom of the screen and marked data-pop-floor (the calendar dock on a phone) is the
window's floor: the window does not go under it.
*/
import { element } from './eventsData';

const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 160;
const GAP = 8;
// A scroll that moves the item less than this (tail of a fling) keeps the window open.
const SCROLL_TOLERANCE = 24;
const HAS_POPOVER = typeof window.HTMLElement?.prototype.showPopover === 'function';
const FOCUSABLE = 'a[href], button:not([disabled])';
const states = new WeakMap();
let active = null;
let listening = false;

/**
 * Wires the details window to the [data-pop] items inside `container`. A second call for the
 * same container (page init after back/forward) only swaps `buildContent`.
 * @param {HTMLElement} container element that holds the items and the window
 * @param {(item: HTMLElement) => HTMLElement|null} buildContent window content for an item
 */
export function bindPopover(container, buildContent) {
    listenToDocument();
    const known = states.get(container);
    if (known) {
        known.buildContent = buildContent;
        return;
    }
    const state = {
        container,
        buildContent,
        timer: 0,
        item: null,
        origin: null,
        preview: false,
        anchor: { top: 0, left: 0 },
        pop: createPop(container),
    };
    states.set(container, state);
    if (window.matchMedia('(hover: hover)').matches) {
        container.addEventListener('pointerover', (event) => onPointerOver(state, event));
        container.addEventListener('pointerout', (event) => onPointerOut(state, event));
        state.pop.addEventListener('pointerenter', () => clearTimeout(state.timer));
        state.pop.addEventListener('pointerleave', () => scheduleClose(state));
    }
    container.addEventListener('click', (event) => onClick(state, event));
    state.pop.addEventListener('focusout', (event) => onFocusOut(state, event));
}

/** Document-wide listeners, once per page load; they act on the one open window. */
function listenToDocument() {
    if (listening) {
        return;
    }
    listening = true;
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && active) {
            close(active, true);
        }
    });
    document.addEventListener('pointerdown', (event) => {
        if (active && !active.pop.contains(event.target) && !event.target.closest?.('[data-pop]')) {
            close(active, false);
        }
    });
    document.addEventListener('scroll', (event) => {
        if (active && !active.pop.contains(event.target) && anchorMoved(active)) {
            close(active, false);
        }
    }, { capture: true, passive: true });
    window.addEventListener('resize', () => active && close(active, false));
}

/** The item has scrolled away — up, down or sideways (the timeline scrolls horizontally). */
function anchorMoved(state) {
    const rect = state.origin?.getBoundingClientRect();
    if (!rect) {
        return true;
    }
    return Math.abs(rect.top - state.anchor.top) > SCROLL_TOLERANCE
        || Math.abs(rect.left - state.anchor.left) > SCROLL_TOLERANCE;
}

function createPop(container) {
    const pop = element('div', 'th-cal-pop');
    pop.id = `th-cal-pop-${Math.random().toString(36).slice(2, 8)}`;
    pop.setAttribute('role', 'dialog');
    pop.tabIndex = -1;
    if (HAS_POPOVER) {
        pop.setAttribute('popover', 'manual');
    } else {
        pop.hidden = true;
    }
    container.append(pop);
    return pop;
}

/** Hover shows a preview; a window opened by click, tap or key is not replaced by hover. */
function onPointerOver(state, event) {
    const item = event.target.closest('[data-pop]');
    if (item === state.item) {
        clearTimeout(state.timer);
        return;
    }
    if (!item || state.pop.contains(item)) {
        return;
    }
    if (isOpen(state) && !state.preview) {
        return;
    }
    clearTimeout(state.timer);
    state.timer = setTimeout(() => open(state, item, false), OPEN_DELAY_MS);
}

function onPointerOut(state, event) {
    const item = event.target.closest('[data-pop]');
    if (item && !state.pop.contains(item) && !item.contains(event.relatedTarget)) {
        scheduleClose(state);
    }
}

/**
 * A click on an item opens its window; a second one on the same item closes it. A hover
 * preview is not "open" yet: a click or Enter/Space on it pins it and moves focus inside.
 * Enter/Space arrive as a click too (event.detail 0). A click on an item inside the window
 * drills down in place.
 */
function onClick(state, event) {
    const item = event.target.closest('[data-pop]');
    if (!item) {
        if (!state.pop.contains(event.target)) {
            close(state, false);
        }
        return;
    }
    if (item === state.item && isOpen(state) && !state.preview) {
        close(state, event.detail === 0);
        return;
    }
    open(state, item, true);
}

function onFocusOut(state, event) {
    const next = event.relatedTarget;
    if (next && !state.pop.contains(next) && next !== state.origin) {
        close(state, false);
    }
}

function scheduleClose(state) {
    clearTimeout(state.timer);
    state.timer = setTimeout(() => {
        if (state.preview) {
            close(state, false);
        }
    }, CLOSE_DELAY_MS);
}

/** @param {boolean} engaged opened by click, tap or key: focus goes in, hover-out keeps it */
function open(state, item, engaged) {
    clearTimeout(state.timer);
    const content = state.buildContent(item);
    if (!content) {
        return;
    }
    if (active && active !== state) {
        close(active, false);
    }
    const inside = state.pop.contains(item);
    state.pop.replaceChildren(content);
    state.pop.setAttribute('aria-label', item.textContent.trim());
    if (!inside) {
        setTrigger(state, item);
    }
    state.item = item;
    state.preview = !engaged;
    show(state);
    place(state.pop, state.origin);
    const { top, left } = state.origin.getBoundingClientRect();
    state.anchor = { top, left };
    active = state;
    if (engaged) {
        (state.pop.querySelector(FOCUSABLE) || state.pop).focus({ preventScroll: true });
    }
}

function setTrigger(state, item) {
    state.origin?.setAttribute('aria-expanded', 'false');
    state.origin = item;
    item.setAttribute('aria-controls', state.pop.id);
    item.setAttribute('aria-expanded', 'true');
}

/** @param {boolean} returnFocus put focus back on the item that opened the window */
function close(state, returnFocus) {
    clearTimeout(state.timer);
    if (!isOpen(state)) {
        return;
    }
    const hadFocus = state.pop.contains(document.activeElement);
    hide(state);
    state.origin?.setAttribute('aria-expanded', 'false');
    if ((returnFocus || hadFocus) && state.origin?.isConnected) {
        state.origin.focus({ preventScroll: true });
    }
    state.item = null;
    state.preview = false;
    if (active === state) {
        active = null;
    }
}

function isOpen(state) {
    return HAS_POPOVER ? state.pop.matches(':popover-open') : !state.pop.hidden;
}

function show(state) {
    if (isOpen(state)) {
        return;
    }
    if (HAS_POPOVER) {
        state.pop.showPopover();
    } else {
        state.pop.hidden = false;
    }
    state.pop.dataset.open = '';
}

function hide(state) {
    delete state.pop.dataset.open;
    if (HAS_POPOVER) {
        state.pop.hidePopover();
    } else {
        state.pop.hidden = true;
    }
}

/**
 * Beside the item, level with its top — to the right, or to the left when the right has no
 * room; on a narrow screen (no room on either side) below it, or above when that fits better.
 * Always kept inside the window (CSS caps the height).
 */
function place(pop, item) {
    const anchor = item.getBoundingClientRect();
    // Measured at the neutral corner: at its old place near an edge the window would be
    // squeezed and measure narrower than it then shows.
    setPosition(pop, 0, 0);
    // Layout size, not getBoundingClientRect: the opening animation scales the window down.
    const size = { width: pop.offsetWidth, height: pop.offsetHeight };
    const side = sideLeft(anchor, size);
    if (side !== null) {
        const top = Math.min(anchor.top, floor() - size.height - GAP);
        setPosition(pop, side, Math.max(GAP, top));
        return;
    }
    const fitsBelow = anchor.bottom + GAP + size.height <= floor();
    const top = fitsBelow ? anchor.bottom + GAP : Math.max(GAP, anchor.top - GAP - size.height);
    const left = Math.min(Math.max(GAP, anchor.left), window.innerWidth - size.width - GAP);
    setPosition(pop, Math.max(GAP, left), top);
}

/** The lowest the window may reach: the top of a shown [data-pop-floor] bar, else the window. */
function floor() {
    return [...document.querySelectorAll('[data-pop-floor]')]
        .map((bar) => bar.getBoundingClientRect())
        .filter((rect) => rect.height > 0)
        .reduce((lowest, rect) => Math.min(lowest, rect.top), window.innerHeight);
}

/** Left edge of the window beside the item: right of it, else left of it; null — no room. */
function sideLeft(anchor, size) {
    if (anchor.right + GAP + size.width <= window.innerWidth - GAP) {
        return anchor.right + GAP;
    }
    if (anchor.left - GAP - size.width >= GAP) {
        return anchor.left - GAP - size.width;
    }
    return null;
}

function setPosition(pop, left, top) {
    pop.style.left = `${Math.round(left)}px`;
    pop.style.top = `${Math.round(top)}px`;
}
